import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useProjects } from "@/hooks/useProjects";
import { ProjectCard } from "./ProjectCard";
import { SplitWords } from "./Motion";

export const WorkSection = () => {
  const { projects, loading, error } = useProjects();
  const [filter, setFilter] = useState("All");
  const cats = useMemo(() => ["All", ...Array.from(new Set(projects.map((p) => p.category)))], [projects]);
  const shown = filter === "All" ? projects : projects.filter((p) => p.category === filter);

  return (
    <section id="work" data-testid="work-section" className="scroll-mt-24 bg-white py-20 sm:py-28">
      <div className="container-x">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <h2 className="max-w-3xl font-display text-4xl font-extrabold tracking-[-0.03em] text-slate-900 sm:text-6xl lg:text-7xl">
            <SplitWords text="Real projects," /> <SplitWords text="real" delay={0.15} marker="#FACC15" /> <SplitWords text="businesses." delay={0.2} />
          </h2>
        </div>

        {cats.length > 2 && (
          <div role="tablist" aria-label="Filter projects" className="mt-10 flex gap-2 overflow-x-auto pb-2">
            {cats.map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={filter === c}
                data-testid={`project-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`relative shrink-0 rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-200 ${filter === c ? "text-white" : "bg-[#F4F1EA] text-slate-700 hover:bg-[#FACC15] hover:text-slate-900"}`}
              >
                {filter === c && <motion.span layoutId="filter-pill" className="absolute inset-0 rounded-full bg-slate-900" transition={{ type: "spring", stiffness: 380, damping: 30 }} />}
                <span className="relative">{c}</span>
              </button>
            ))}
          </div>
        )}

        {loading && <div data-testid="work-loading" className="mt-10 grid gap-6 lg:grid-cols-2">{[0, 1].map((i) => <div key={i} className="h-[380px] animate-pulse rounded-[28px] bg-[#F4F1EA]" />)}</div>}
        {!loading && error && <p data-testid="work-error" className="mt-10 rounded-2xl bg-[#FFF1F2] p-6 text-slate-700">Projects couldn't load right now. Please refresh the page.</p>}

        <div data-testid="project-grid" className="mt-8 grid grid-flow-row-dense grid-cols-1 gap-6 lg:grid-cols-12">
          <AnimatePresence initial={false}>
            {shown.map((p) => <ProjectCard key={p.id} p={p} />)}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
